// Helper to synthesize a small PCM 16-bit mono WAV as base64 (browser-only)
// WebexMockVoicemailService.ts
// Contains all mock voicemail logic for Webex Calling in developer mode only
import {
  IVoiceMailList,
  IVoiceMailResponse,
  IVoiceMailTranscriptMessage,
  IVoiceMessageContentResponse,
  RESPONSE_STATUS,
  VoicemailTranscriptFetchStatus
} from '../../../adapter-interfaces/src';

// Helper: Simulate network delay
function delay(ms: number) { return new Promise(resolve => setTimeout(resolve, ms)); }

// Mock voicemail data
const now = Date.now();
export const MOCK_VOICEMAILS: IVoiceMailList[] = Array.from({ length: 40 }, (_, i) => {
  const idx = i + 1;
  const names = [
    'Alice Adams', 'Bob Brown', 'Charlie Clark', 'Dana Diaz', 'Evan Evans', 'Frank Foster', 'Grace Green', 'Henry Hall',
    'Ivy Irving', 'Jack Jones', 'Karen King', 'Liam Lewis', 'Mona Moore', 'Nina Nash', 'Oscar Owen', 'Paul Price',
    'Quinn Queen', 'Rita Reed', 'Sam Smith', 'Tina Turner', 'Uma Underwood', 'Vera Vance', 'Will White', 'Xander Xu',
    'Yara Young', 'Zane Ziegler', 'Amy Allen', 'Ben Bell', 'Cory Cole', 'Drew Drake', 'Elle East', 'Finn Fox',
    'Gina Grant', 'Hank Hope', 'Iris Ivy', 'Jake Jay', 'Kara Kent', 'Leo Lane', 'Mia Miles', 'Nate North'
  ];
  const name = names[i % names.length];
  const userId = name.toLowerCase().replace(/ /g, '.') + '@example.com';
  const address = `tel:+15550${100 + idx}`;
  const duration = String(10000 + (idx * 1000) % 90000);
  const time = String(now - 1000 * 60 * 60 * idx);
  const messageId = `/v2.0/user/mock/VoiceMessagingMessages/vm-${idx}`;
  const read = idx % 3 === 0 ? 'true' : undefined;
  return {
    duration: { $: duration },
    callingPartyInfo: {
      name: { $: name },
      userId: { $: userId },
      address: { $: address },
    },
    time: { $: time },
    messageId: { $: messageId },
    ...(read ? { read } : {}),
  } as unknown as IVoiceMailList;
});

export async function fetchMockVoicemailsBatch(offset: number, limit: number): Promise<IVoiceMailResponse> {
  await delay(300);
  console.log('fetchMockVoicemailsBatch called with offset=', offset, 'limit=', limit);
  const slice = MOCK_VOICEMAILS.slice(offset, offset + limit);
  return {
    statusCode: 200,
    message: 'Mocked voicemail list',
    data: { voicemailList: slice },
  } as IVoiceMailResponse;
}

export async function getMockVoiceMessage(voicemailId: string): Promise<IVoiceMessageContentResponse> {
  await delay(200);
  const idTail = (voicemailId || '').split('/').pop() || voicemailId;
  const freqMap: Record<string, number> = {
    'vm-1': 440,
    'vm-2': 523,
    'vm-3': 587,
    'vm-4': 659,
    'vm-5': 784,
  };
  const frequency = freqMap[idTail] || 440;
  const base64 = synthesizeWavBase64(frequency, 350, 8000);
  return {
    statusCode: 200,
    message: RESPONSE_STATUS.SUCCESS,
    data: {
      voicemailContent: {
        type: 'audio/wav',
        content: base64,
      },
    },
    voiceMessageId: voicemailId,
    createdAt: new Date().toUTCString(),
  } as IVoiceMessageContentResponse;
}

export async function getMockVMTranscript(voicemailId: string): Promise<IVoiceMailTranscriptMessage> {
  await delay(250);
  const isNA = voicemailId === 'vm-3';
  return {
    statusCode: 200,
    message: isNA ? VoicemailTranscriptFetchStatus.NA : VoicemailTranscriptFetchStatus.READY,
    data: {
      voicemailTranscript: isNA ? '' : `Mock transcript for ${voicemailId}: Lorem ipsum dolor sit amet, consectetur adipiscing elit.`,
    },
    voiceMessageId: voicemailId,
    createdAt: new Date().toUTCString(),
  } as IVoiceMailTranscriptMessage;
}

// Helper to synthesize a small PCM 16-bit mono WAV as base64 (browser-only)
function synthesizeWavBase64(frequency = 440, durationMs = 300, sampleRate = 8000): string {
  const numSamples = Math.max(1, Math.floor((sampleRate * durationMs) / 1000));
  const bytesPerSample = 2; // 16-bit PCM
  const subchunk2Size = numSamples * bytesPerSample;
  const chunkSize = 36 + subchunk2Size;
  const buffer = new ArrayBuffer(44 + subchunk2Size);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, chunkSize, true);
  writeString(view, 8, 'WAVE');

  // fmt chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // Audio format = 1 (PCM)
  view.setUint16(22, 1, true); // Num channels = 1 (mono)
  view.setUint32(24, sampleRate, true); // sample rate
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 8 * bytesPerSample, true); // bits per sample = 16

  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, subchunk2Size, true);

  // Write samples (simple sine wave)
  let offset = 44;
  const amplitude = 0.25 * 0x7fff;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const sample = Math.round(Math.sin(2 * Math.PI * frequency * t) * amplitude);
    view.setInt16(offset, sample, true);
    offset += 2;
  }

  // Convert to base64
  const u8 = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    const sub = u8.subarray(i, i + chunk);
    binary += String.fromCharCode.apply(null, Array.from(sub));
  }
  return btoa(binary);
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
